import { useState } from 'react';
import { C } from '../styles/colours.js';
import { supabase } from '../supabase.js';

function Profile({ onBack, currentUser, isPaid, onUpgrade }) {
  const [name, setName] = useState(currentUser?.user_metadata?.name || currentUser?.email?.split('@')[0] || '');
  const [editing, setEditing] = useState(false);
  const [newName, setNewName] = useState(name);
  const [changingPwd, setChangingPwd] = useState(false);
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [pwdMsg, setPwdMsg] = useState('');
  const [saving, setSaving] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  const getInitial = () => name.charAt(0).toUpperCase();
  const plan = currentUser?.user_metadata?.plan || 'free';

  const getPlanLabel = () => {
    if (!isPaid) return null;
    if (plan === 'day') return '☀️ Day Pass';
    if (plan === 'month') return '📅 Monthly Plan';
    if (plan === 'year') return '🏆 Yearly Plan';
    return '✅ Pro Plan';
  };

  const saveName = async () => {
    if (!newName.trim()) return;
    setSaving(true);
    const { error } = await supabase.auth.updateUser({
      data: { name: newName.trim() }
    });
    setSaving(false);
    if (!error) {
      setName(newName.trim());
      setEditing(false);
    }
  };

  const savePassword = async () => {
    if (newPwd.length < 6) { setPwdMsg('Password must be at least 6 characters'); return; }
    if (newPwd !== confirmPwd) { setPwdMsg('Passwords do not match'); return; }
    setSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPwd });
    setSaving(false);
    if (error) {
      setPwdMsg(error.message);
    } else {
      setPwdMsg('✅ Password updated!');
      setNewPwd('');
      setConfirmPwd('');
      setChangingPwd(false);
    }
  };

  const inputStyle = {
    width: '100%', background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 10, padding: '12px 14px',
    fontSize: 15, color: C.white,
    outline: 'none', fontFamily: 'sans-serif',
    boxSizing: 'border-box',
  };

  return (
    <div style={{ background: C.bg, minHeight: '100vh', fontFamily: 'sans-serif', color: C.white, padding: '20px', maxWidth: 480, margin: '0 auto' }}>

      {/* Top bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 28 }}>
        <button onClick={onBack} style={{ background: C.card, border: 'none', color: C.textSub, padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>← Back</button>
        <div style={{ fontSize: 13, fontWeight: 700, color: C.textSub, letterSpacing: 1 }}>MY PROFILE</div>
        <div style={{ width: 60 }} />
      </div>

      {/* Avatar */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: 28 }}>
        <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(145deg, #e8526a, #c0392b)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 900, fontSize: 36, color: '#fff', marginBottom: 12 }}>
          {getInitial()}
        </div>
        <div style={{ fontSize: 20, fontWeight: 900 }}>{name}</div>
        <div style={{ fontSize: 13, color: C.textSub, marginTop: 4 }}>{currentUser?.email}</div>
        <div style={{ marginTop: 10 }}>
          {isPaid ? (
            <div style={{ background: `${C.green}20`, border: `1px solid ${C.green}40`, borderRadius: 20, padding: '5px 16px', fontSize: 12, fontWeight: 800, color: C.green }}>
              {getPlanLabel()}
            </div>
          ) : (
            <div style={{ background: `${C.gold}20`, border: `1px solid ${C.gold}40`, borderRadius: 20, padding: '5px 16px', fontSize: 12, fontWeight: 800, color: C.gold }}>
              ⭐ Free Plan — first game free
            </div>
          )}
        </div>
      </div>

      {/* Name section */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '16px', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 10 }}>DISPLAY NAME</div>
        {editing ? (
          <>
            <input type="text" value={newName} onChange={e => setNewName(e.target.value)} style={{ ...inputStyle, marginBottom: 10 }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={saveName} disabled={saving} style={{ flex: 1, background: C.gold, color: '#000', border: 'none', borderRadius: 10, padding: '11px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                {saving ? 'Saving...' : '✅ Save'}
              </button>
              <button onClick={() => { setEditing(false); setNewName(name); }} style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: C.white, borderRadius: 10, padding: '11px', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 16, fontWeight: 700 }}>{name}</div>
            <button onClick={() => setEditing(true)} style={{ background: `${C.gold}20`, border: `1px solid ${C.gold}40`, color: C.gold, borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              ✏️ Edit
            </button>
          </div>
        )}
      </div>

      {/* Email section */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '16px', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 6 }}>EMAIL</div>
        <div style={{ fontSize: 16, color: C.textSub }}>{currentUser?.email}</div>
      </div>

      {/* Password section */}
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: '16px', marginBottom: 12 }}>
        <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 1, color: C.textMuted, marginBottom: 10 }}>PASSWORD</div>
        {changingPwd ? (
          <>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>NEW PASSWORD</div>
              <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 10, overflow: 'hidden' }}>
                <input type={showPwd ? 'text' : 'password'} placeholder="••••••••" value={newPwd} onChange={e => setNewPwd(e.target.value)}
                  style={{ flex: 1, background: 'transparent', border: 'none', padding: '12px 14px', fontSize: 15, color: C.white, outline: 'none', fontFamily: 'sans-serif' }} />
                <button onClick={() => setShowPwd(!showPwd)} style={{ background: 'none', border: 'none', color: C.textSub, cursor: 'pointer', padding: '0 14px', fontSize: 16 }}>{showPwd ? '🙈' : '👁️'}</button>
              </div>
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, color: C.textMuted, marginBottom: 6 }}>CONFIRM PASSWORD</div>
              <input type="password" placeholder="••••••••" value={confirmPwd} onChange={e => setConfirmPwd(e.target.value)} style={inputStyle} />
            </div>
            {pwdMsg && (
              <div style={{ fontSize: 12, color: pwdMsg.includes('✅') ? C.green : C.red, marginBottom: 10, fontWeight: 600 }}>{pwdMsg}</div>
            )}
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={savePassword} disabled={saving} style={{ flex: 1, background: C.gold, color: '#000', border: 'none', borderRadius: 10, padding: '11px', fontSize: 13, fontWeight: 800, cursor: 'pointer' }}>
                {saving ? 'Saving...' : '✅ Update'}
              </button>
              <button onClick={() => { setChangingPwd(false); setNewPwd(''); setConfirmPwd(''); setPwdMsg(''); }} style={{ flex: 1, background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: C.white, borderRadius: 10, padding: '11px', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 16, color: C.textSub }}>••••••••</div>
            <button onClick={() => setChangingPwd(true)} style={{ background: `${C.blue}20`, border: `1px solid ${C.blue}40`, color: C.blue, borderRadius: 8, padding: '6px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
              🔑 Change
            </button>
          </div>
        )}
      </div>

      {/* Plan section — Free users */}
      {!isPaid && (
        <div style={{ background: `linear-gradient(135deg, ${C.gold}15, ${C.gold}05)`, border: `1px solid ${C.gold}30`, borderRadius: 16, padding: '16px', marginBottom: 12, textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.gold, marginBottom: 6 }}>⭐ Upgrade to Pro</div>
          <div style={{ fontSize: 13, color: C.textSub, marginBottom: 14 }}>Unlock unlimited games and all features.</div>
          <button onClick={() => { onBack(); onUpgrade(); }} style={{ width: '100%', background: 'linear-gradient(135deg, #FFD166, #f0a500)', color: '#000', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            ⭐ See Plans & Upgrade
          </button>
        </div>
      )}

      {/* Plan section — Day pass users can upgrade */}
      {isPaid && plan === 'day' && (
        <div style={{ background: `linear-gradient(135deg, ${C.purple}15, ${C.purple}05)`, border: `1px solid ${C.purple}30`, borderRadius: 16, padding: '16px', marginBottom: 12, textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 800, color: C.purple, marginBottom: 6 }}>🚀 Upgrade to Monthly or Yearly</div>
          <div style={{ fontSize: 13, color: C.textSub, marginBottom: 14 }}>Get unlimited access every day.</div>
          <button onClick={() => { onBack(); onUpgrade(); }} style={{ width: '100%', background: 'linear-gradient(135deg, #9b5de5, #7b2fe0)', color: '#fff', border: 'none', borderRadius: 12, padding: '14px', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
            🚀 Upgrade Plan
          </button>
        </div>
      )}

      {/* App info */}
      <div style={{ textAlign: 'center', marginTop: 20, marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: C.textMuted }}>TabliQ — The Table Quiz</div>
        <div style={{ fontSize: 11, color: C.textMuted, marginTop: 4 }}>tabliq.io</div>
      </div>

    </div>
  );
}

export default Profile;